import React, { useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import SvgCalendar from '@icons/calendar.svg?react';
import SvgInfo from '@icons/info.svg?react';
import SvgAttachment from '@icons/attachment.svg?react';

interface RenderProps {
  showEtransport: boolean;
  showInternalInfo: boolean;
}

export type RingilReservationTab = 'reservation' | 'internalinfo' | 'attachments' | 'eTransport';

/**
 * The reservation detail's tabs. Each one switches the pane below; the strip is the shared `Tabs`, so tabs that
 * don't fit (the reservation list's split pane) fold into "…". A render function, not a component made inside
 * the hook, which would remount the strip on every render.
 */
export const useCreateRingilReservationTabs = () => {
  const { tr } = useTranslationLgs();
  const [selectedPanel, setSelectedPanel] = useState<RingilReservationTab>('reservation');

  const renderTabs = ({ showEtransport, showInternalInfo }: RenderProps) => {
    const allPanes: { pane: RingilReservationTab; shown: boolean; tab: Omit<TabProps, 'active'> }[] = [
      { pane: 'reservation', shown: true, tab: { icon: <SvgCalendar />, label: tr(`RingilTabs.expedition`, `Expedition`), dataTestId: 'ringilTab-shipment' } },
      { pane: 'eTransport', shown: showEtransport, tab: { icon: <SvgInfo />, label: tr(`RingilTabs.eTransprt`, `eTransport`), dataTestId: 'ringilTab-eTransport' } },
      {
        pane: 'internalinfo',
        shown: showInternalInfo,
        tab: { icon: <SvgInfo />, label: tr(`RingilTabs.internalInfo`, `Internal info`), dataTestId: 'ringilTab-internalInfoAndHistory' },
      },
      { pane: 'attachments', shown: true, tab: { icon: <SvgAttachment />, label: tr(`RingilTabs.attachments`, `Attachments`), dataTestId: 'ringilTab-attachments' } },
    ];
    const panes = allPanes.filter(entry => entry.shown);

    return <Tabs tabs={panes.map(({ pane, tab }) => ({ ...tab, active: selectedPanel === pane }))} onTabChange={index => setSelectedPanel(panes[index].pane)} />;
  };

  return { renderTabs, selectedPanel };
};
